import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  Badge,
} from "@asm/ui";
import {
  Globe,
  Clock,
  Shield,
  Mail,
  Activity,
  CheckCircle2,
  XCircle,
  Pause,
  Target,
} from "lucide-react";
import { adminApi, type ReconStats, type ReconConfig, type ReconCampaign } from "@/lib/api";

interface ReconOverviewProps {
  stats: ReconStats;
  config: ReconConfig;
  onToggle: (enabled: boolean) => Promise<void>;
}

export function ReconOverview({ stats, config, onToggle }: ReconOverviewProps) {
  const [toggling, setToggling] = useState(false);
  const [activeCampaigns, setActiveCampaigns] = useState<ReconCampaign[]>([]);

  useEffect(() => {
    adminApi
      .listCampaigns({ status: "active", per_page: 10 })
      .then((data) => setActiveCampaigns(data.campaigns))
      .catch(() => {});
  }, [stats]);

  const handlePauseCampaign = async (id: string) => {
    try {
      await adminApi.pauseCampaign(id);
      setActiveCampaigns((prev) => prev.filter((c) => c.id !== id));
    } catch {
      // handled silently — overview is informational
    }
  };

  const handleToggle = async () => {
    setToggling(true);
    try {
      await onToggle(!stats.enabled);
    } finally {
      setToggling(false);
    }
  };

  const progressPercent = stats.max_per_hour > 0
    ? Math.min(100, (stats.scanned_this_hour / stats.max_per_hour) * 100)
    : 0;

  return (
    <div className="space-y-6">
      {/* Status Banner */}
      <Card className={stats.enabled ? "border-green-500/50" : "border-orange-500/50"}>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {stats.enabled ? (
                <CheckCircle2 className="h-8 w-8 text-green-500" />
              ) : (
                <XCircle className="h-8 w-8 text-orange-500" />
              )}
              <div>
                <h3 className="text-lg font-semibold">
                  Recon System {stats.enabled ? "Active" : "Disabled"}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {stats.enabled
                    ? "Background reconnaissance is running"
                    : "Enable to start passive reconnaissance"}
                </p>
              </div>
            </div>
            <Button
              variant={stats.enabled ? "destructive" : "default"}
              onClick={handleToggle}
              disabled={toggling}
            >
              {toggling ? "..." : stats.enabled ? "Disable Recon" : "Enable Recon"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Rate Limit Progress */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Hourly Scan Progress
          </CardTitle>
          <CardDescription>
            {stats.scanned_this_hour} / {stats.max_per_hour} domains this hour
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span>Progress</span>
              <span className="font-medium">
                {progressPercent.toFixed(0)}%
                {stats.is_rate_limited && (
                  <Badge variant="destructive" className="ml-2">Rate Limited</Badge>
                )}
              </span>
            </div>
            <div className="h-2 rounded-full bg-muted overflow-hidden">
              <div
                className={`h-full transition-all ${
                  stats.is_rate_limited ? "bg-destructive" : "bg-primary"
                }`}
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Active Campaigns */}
      {activeCampaigns.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="h-5 w-5" />
              Active Campaigns
            </CardTitle>
            <CardDescription>
              {activeCampaigns.length} campaign{activeCampaigns.length !== 1 ? "s" : ""} being processed by the scheduler
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {activeCampaigns.map((campaign) => (
                <div
                  key={campaign.id}
                  className="flex items-center justify-between rounded-md border px-4 py-2"
                >
                  <div className="flex items-center gap-3">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                    </span>
                    <div>
                      <span className="font-medium text-sm">{campaign.name}</span>
                      <span className="text-xs text-muted-foreground ml-2">
                        {campaign.scanned_count}/{campaign.domain_count} scanned
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">
                      P{campaign.priority}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handlePauseCampaign(campaign.id)}
                      title="Pause campaign"
                    >
                      <Pause className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Domains</CardTitle>
            <Globe className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total_domains.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              {stats.unique_tlds} unique TLDs
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Scanned</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.scanned_domains.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              {stats.scanned_last_24h} in last 24h
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending</CardTitle>
            <Clock className="h-4 w-4 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.pending_domains.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              Awaiting scan
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Results</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total_results.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              {stats.scanned_last_7d} in last 7 days
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Security Scores */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Email Security Score</CardTitle>
            <Mail className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <div className="text-3xl font-bold">
                {stats.avg_email_security_score.toFixed(0)}
              </div>
              <div className="flex-1">
                <div className="h-2 rounded-full bg-muted overflow-hidden">
                  <div
                    className={`h-full ${
                      stats.avg_email_security_score >= 80 ? "bg-green-500" :
                      stats.avg_email_security_score >= 50 ? "bg-yellow-500" :
                      "bg-red-500"
                    }`}
                    style={{ width: `${stats.avg_email_security_score}%` }}
                  />
                </div>
              </div>
              <span className="text-sm text-muted-foreground">/100</span>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              SPF, DKIM, DMARC compliance
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Security Headers Score</CardTitle>
            <Shield className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <div className="text-3xl font-bold">
                {stats.avg_security_headers_score.toFixed(0)}
              </div>
              <div className="flex-1">
                <div className="h-2 rounded-full bg-muted overflow-hidden">
                  <div
                    className={`h-full ${
                      stats.avg_security_headers_score >= 80 ? "bg-green-500" :
                      stats.avg_security_headers_score >= 50 ? "bg-yellow-500" :
                      "bg-red-500"
                    }`}
                    style={{ width: `${stats.avg_security_headers_score}%` }}
                  />
                </div>
              </div>
              <span className="text-sm text-muted-foreground">/100</span>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              CSP, HSTS, X-Frame-Options, etc.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Configuration Summary */}
      <Card>
        <CardHeader>
          <CardTitle>Current Configuration</CardTitle>
          <CardDescription>Active modules and rate limits</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <h4 className="font-medium mb-2">Active Modules</h4>
              <div className="flex flex-wrap gap-2">
                {config.modules.map((mod) => (
                  <Badge key={mod} variant="secondary">
                    {mod}
                  </Badge>
                ))}
              </div>
            </div>
            <div>
              <h4 className="font-medium mb-2">Target TLDs</h4>
              <div className="flex flex-wrap gap-2">
                {config.target_tlds.length > 0 ? (
                  config.target_tlds.map((tld) => (
                    <Badge key={tld} variant="outline">
                      .{tld}
                    </Badge>
                  ))
                ) : (
                  <span className="text-sm text-muted-foreground">All TLDs</span>
                )}
              </div>
            </div>
            <div>
              <h4 className="font-medium mb-2">Rate Limits</h4>
              <div className="text-sm space-y-1">
                <div>Max domains/hour: <span className="font-mono">{config.max_domains_per_hour}</span></div>
                <div>DNS QPS: <span className="font-mono">{config.rate_limit_dns_qps}</span></div>
                <div>HTTP QPS: <span className="font-mono">{config.rate_limit_http_qps}</span></div>
                <div>GitHub API QPS: <span className="font-mono">{config.rate_limit_github_qps}</span></div>
                <div>External API QPS: <span className="font-mono">{config.rate_limit_external_api_qps}</span></div>
              </div>
            </div>
            <div>
              <h4 className="font-medium mb-2">Schedule</h4>
              <div className="text-sm space-y-1">
                <div>Re-scan interval: <span className="font-mono">{config.scan_interval_hours}h</span></div>
                <div>Retention: <span className="font-mono">{config.retention_days} days</span></div>
                <div>Min idle workers: <span className="font-mono">{config.min_idle_workers}</span></div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
